import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ui } from '@procuros/datachecks';
import { TFunction } from 'i18next';

import { useCanEditMessage } from 'hooks/useCanEditMessage';
import { useCapabilities } from 'hooks/useCapabilities/useCapabilities';
import { useFlattenDataAndUIConfig } from 'hooks/useFlattenDataAndUIConfig';
import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { logError } from 'services/logging/logging';
import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { DisplayModes } from 'services/webedi/types';
import { useInitWebEDIStore } from 'services/webedi/webedi';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { getAcknowledgedAnnotationIds } from 'stores/annotationAcknowledgments/annotationAcknowledgments';
import {
  setDataStores,
  setDestinationProcess,
  setInitialCanonical,
  setMessageState,
  setProcessSpecificationFields,
} from 'stores/webedi/webedi';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { GenericError } from 'support/helpers/errors/errors';
import { getOriginalPDF } from 'support/helpers/messages/messages';
import { routeToPage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import {
  DocumentType,
  MessageV2DTO,
  PartnerDTO,
  ProcessSpecificationDTO,
  TransactionInternalType,
  TransactionOriginApp,
  TransactionsRoutes,
  TransactionState,
  UserDTO,
} from 'support/types';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page } from 'components/Page/Page';
import { deriveDocumentDisplayConfig } from 'components/WebEDI/DocumentDisplayConfig/deriveDocumentDisplayConfig';
import { DocumentDisplayConfigProvider } from 'components/WebEDI/DocumentDisplayConfig/DocumentDisplayConfigContext';
import { PDFReferenceSidebar } from 'components/WebEDI/WebEDISidebar/components/PDFReferenceSidebar';

import { useEditMessage } from '../hooks/useEditMessage/useEditMessage';
import { SendConfirmationModalFlow } from '../components/SendConfirmationModalFlow/SendConfirmationModalFlow';
import { EditMessageContent } from '../EditMessageContent/EditMessageContent';
import { revealPartyFields } from '../helpers';
import { MessageHeader } from '../TransactionViewPage/MessageViewPage/MessageHeader/MessageHeader';
import { UnknownTypePlaceholder } from '../TransactionViewPage/MessageViewPage/UnknownTypePlaceholder';
import { EditNoLongerPossibleModal } from './components/EditNoLongerPossibleModal/EditNoLongerPossibleModal';
import { canSubmitMessageEdit, hasMessageAlreadyBeenUpdated, isEditableMessageState } from './helpers';

export function EditDocument() {
  useInitWebEDIStore();
  const { id } = useParams<{ id: string }>();
  const { data: currentPartner } = useCurrentPartner();
  const { data: currentUser } = useCurrentUser();
  const { data: message, isError } = useMessageWithProcessSpecification({
    variables: {
      messageId: id,
    },
  });

  useCapabilities({ messageId: id });

  // Fetch the latest message directly, bypassing the query cache: writing it back would
  // reset the edit form mid-submit (values: data) and tear down the in-flight modal.
  const onRefetchMessage = useCallback(async () => {
    const latestMessage = await useMessageWithProcessSpecification.queryFn({
      queryKey: useMessageWithProcessSpecification.getKey({ messageId: id }),
      meta: undefined,
    });
    return latestMessage?.data;
  }, [id]);

  const fields = useMemo(() => {
    if (!message?.fields || !currentUser?.isAdmin) {
      return message?.fields;
    }
    return revealPartyFields(message.fields);
  }, [message?.fields, currentUser?.isAdmin]);

  const {
    data,
    uiConfig,
    isLoading: isLoadingDataChecks,
  } = useFlattenDataAndUIConfig(fields, message?.data?.canonical);

  useEffect(() => {
    setProcessSpecificationFields(fields);
    setDataStores(message?.data_stores);

    if (message?.data.canonical) {
      setInitialCanonical(message?.data.canonical);
    }

    if (message?.process) {
      setDestinationProcess(message?.process);
    }
    if (message?.data.state) {
      setMessageState(message.data.state as TransactionState);
    }
  }, [message, fields]);

  if (!id) {
    return;
  }

  if (isError) {
    return <UnknownTypePlaceholder messageId={id} />;
  }

  if (isLoadingDataChecks) {
    return <Page isLoading={isLoadingDataChecks}>{null}</Page>;
  }

  // A message is editable while it sits in a failed state, or while an order-agent
  // review is still pending; once it has moved on (routed, superseded, reviewed, …)
  // editing would clobber in-flight processing.
  if (message?.data && !isEditableMessageState(message.data)) {
    return <Navigate to={routeToTransactionPage(TransactionInternalType.message, id)} replace />;
  }

  // Keyed by the route id for the same reason as the message view page, though defensively: every
  // entry here comes from a different route, and UnsavedChangesModal blocks navigation while this is
  // mounted, so an id change in place is not reachable today. Kept so it stays correct if it becomes
  // reachable (e.g. a message-to-message link within the edit route).
  return (
    <EditDocumentPageUI
      key={id}
      message={message?.data}
      messageOriginApp={message?.origin_app}
      data={data}
      relatedTransactions={message?.related_messages || []}
      currentPartner={currentPartner}
      uiConfig={uiConfig}
      processSpecFields={fields}
      currentUser={currentUser}
      onRefetchMessage={onRefetchMessage}
    />
  );
}

type EditDocumentPageUIProps = {
  message: MessageV2DTO | undefined;
  messageOriginApp: TransactionOriginApp | undefined;
  data: Record<string, unknown>;
  relatedTransactions: Array<MessageV2DTO>;
  currentPartner: PartnerDTO | undefined;
  uiConfig: Array<ui.Section> | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  currentUser: UserDTO | undefined;
  onRefetchMessage: () => Promise<MessageV2DTO | undefined>;
};

const DISPLAY_MODE = DisplayModes.edit;

export const EditDocumentPageUI = ({
  message,
  messageOriginApp,
  data,
  uiConfig,
  relatedTransactions,
  currentPartner,
  processSpecFields,
  currentUser,
  onRefetchMessage,
}: EditDocumentPageUIProps) => {
  const navigate = useNavigate();

  const canEditMessage = useCanEditMessage(message, currentUser);
  const displayConfig = deriveDocumentDisplayConfig(message);
  const originalPDF = getOriginalPDF(messageOriginApp, message);
  const isOrderAgentReview = message?.review?.type === 'document_requires_manual_review';

  const handleEditSuccess = useCallback(() => {
    navigate(routeToTransactionPage(TransactionInternalType.message, message?.id as string));
  }, [navigate, message?.id]);

  const {
    editMessage,
    isUpdating: isEditingMessage,
    isSuccess,
  } = useEditMessage({
    message,
    processSpecificationFields: processSpecFields,
    isAdmin: currentUser?.isAdmin,
    onSuccess: handleEditSuccess,
  });

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<Record<string, unknown> | null>(null);
  const [isEditNoLongerPossibleModalOpen, setIsEditNoLongerPossibleModalOpen] = useState(false);
  const [shouldBlockUnsavedChanges, setShouldBlockUnsavedChanges] = useState(true);

  const onSubmit = useCallback((formValues: Record<string, unknown>) => {
    const firstUnresolved = WebEDIErrorsServiceSingleton.getFirstUnresolvedAnnotation();
    if (firstUnresolved) {
      WebEDIErrorsServiceSingleton.setCurrentItem(firstUnresolved);
      return;
    }

    setPendingFormValues(formValues);
    setConfirmationModalOpen(true);
  }, []);

  const handleSendSubmit = useCallback(
    async (submittedAnnotationIds: Array<string>) => {
      setConfirmationModalOpen(false);
      if (!pendingFormValues) {
        return;
      }

      const latestMessage = await onRefetchMessage();

      if (!canSubmitMessageEdit({ originalMessage: message, latestMessage, currentUser, currentPartner })) {
        if (hasMessageAlreadyBeenUpdated(message, latestMessage)) {
          logError(
            new GenericError('WebEDI: edit blocked because the message was already updated', {
              messageId: message?.id,
              originalUpdatedAt: message?.updated_at,
              latestUpdatedAt: latestMessage?.updated_at,
            }),
          );
        }
        setIsEditNoLongerPossibleModalOpen(true);
        return;
      }

      const ackAnnotationIds = getAcknowledgedAnnotationIds();
      editMessage(pendingFormValues, Array.from(new Set([...submittedAnnotationIds, ...ackAnnotationIds])));
    },
    [currentPartner, currentUser, editMessage, message, onRefetchMessage, pendingFormValues],
  );

  const handleCancelConfirmation = () => setConfirmationModalOpen(false);

  const handleEditNoLongerPossibleConfirm = useCallback(async () => {
    setIsEditNoLongerPossibleModalOpen(false);
    setPendingFormValues(null);
    setShouldBlockUnsavedChanges(false);

    await onRefetchMessage();

    handleEditSuccess();
  }, [handleEditSuccess, onRefetchMessage]);

  const handleEditNoLongerPossibleCancel = useCallback(() => {
    setIsEditNoLongerPossibleModalOpen(false);
  }, []);

  return (
    <DocumentDisplayConfigProvider config={displayConfig}>
      <EditMessageContent
        message={message}
        data={data}
        uiConfig={uiConfig}
        processSpecFields={processSpecFields}
        currentPartner={currentPartner}
        currentUser={currentUser}
        canEditMessage={canEditMessage}
        onSubmit={onSubmit}
        isSubmitting={isEditingMessage}
        actionBar={
          !!message && (
            <MessageHeader
              mode={DISPLAY_MODE}
              message={message}
              messageOriginApp={null}
              canDownloadOriginalFile={false}
              relatedTransactions={relatedTransactions}
              currentPartner={currentPartner}
              isLoading={isEditingMessage}
            />
          )
        }
        sidebar={
          originalPDF ? <PDFReferenceSidebar pdfUrl={originalPDF.url} filename={originalPDF.filename} /> : undefined
        }
        modals={
          <>
            <UnsavedChangesModal shouldBlock={!isSuccess && shouldBlockUnsavedChanges} />
            {message ? (
              <SendConfirmationModalFlow
                open={confirmationModalOpen}
                message={message}
                onSubmit={handleSendSubmit}
                onCancel={handleCancelConfirmation}
                showSummary={!isOrderAgentReview}
              />
            ) : null}
            <EditNoLongerPossibleModal
              open={isEditNoLongerPossibleModalOpen}
              onConfirm={handleEditNoLongerPossibleConfirm}
              onCancel={handleEditNoLongerPossibleCancel}
            />
          </>
        }
      />
    </DocumentDisplayConfigProvider>
  );
};

EditDocument.loader = createLoader(
  ({ params }) => useMessageWithProcessSpecification.getKey({ messageId: params.id }),
  useMessageWithProcessSpecification.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useMessageWithProcessSpecification.queryFn>>;
EditDocument.handle = createHandle<ResponseType, RouteParams<TransactionsRoutes.edit>>((params, data) => ({
  label: (t: TFunction) =>
    t('webedi:editDocument.breadcrumb', {
      externalReference: data?.data.external_message_ref || EMPTY_FIELD,
      documentType: t(`common:messageTypes.singular.${data?.data.type || DocumentType.unknown}`),
    }),
  to: routeToPage(TransactionsRoutes.edit, { id: params.id }),
  key: `edit-document`,
}));
